import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, switchMap } from 'rxjs';
import { AuthService } from '@/services/auth.service';
import { Router } from '@angular/router';
import { ToastService } from '@/shared/components';

/**
 * HTTP Interceptor to handle errors and token refresh
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastService = inject(ToastService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized - Token expired
      if (error.status === 401 && !req.url.includes('/auth/')) {
        return authService.refreshToken().pipe(
          switchMap(() => {
            // Retry original request with new token
            const token = authService.getToken();
            const clonedRequest = req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`
              }
            });
            return next(clonedRequest);
          }),
          catchError(refreshError => {
            // Refresh failed, logout user
            authService.logout();
            router.navigate(['/login']);
            return throwError(() => refreshError);
          })
        );
      }

      // Handle 403 Forbidden - Permissão negada
      if (error.status === 403) {
        const errorMessage = error.error?.message || 'Acesso negado. Você não tem permissão para realizar esta ação.';
        
        // Exibir mensagem de erro sem redirecionar bruscamente
        toastService.error(errorMessage);
        
        console.error('Access denied:', error.url, errorMessage);
        
        // Não redirecionar automaticamente - deixar o componente decidir o que fazer
        // Apenas redirecionar se for uma rota não administrativa
        if (error.url && !error.url.includes('/admin/') && !error.url.includes('/evaluator/')) {
          router.navigate(['/']);
        }
      }

      // Handle 404 Not Found
      if (error.status === 404) {
        console.error('Resource not found');
      }

      // Handle 500 Server Error
      if (error.status >= 500) {
        console.error('Server error occurred');
      }

      return throwError(() => error);
    })
  );
};
